import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getAllProductTypeProcesses, getAllAdditionalVariables, getAllSubBrand } from '../../../../services/SystemMaster.services';
import { getLicenseDetails, getLikenessDetails, getTypeBasedCatgeory } from '../../../SystemMasters/Store/SystemMasters.action';
import { fetchSuppliersFactories } from '../../../Library/BomLibrary/store/BomLibrary.action';
import { getAllGradings } from '../../../Library/Pom/Store/Pom.action';
import { transportArray, createOptions, monthsPlaceholder, getYears, converDropdownData, CountriesList } from '../../../../Components/helper';
import { DynamicColumn, MultiCustomAttribute, RefEntity, SelectOptionMap, TableRow } from './types';
import type { GradingLibraryEntry } from './multiEdit';
import { fetchMaster } from '../../../../store/master-registry';
import {
  CascadeSources,
  cat3For,
  cat4For,
  factoriesFor,
  likenessCacheKey,
  resolveRowOptions,
  ScopedAdditionalVariable,
  sizesFor,
  toOptions,
} from './cascadeOptions';
import type { AdditionalVariable, NestedThunkEnvelope } from '../CreateProduct/createProduct.interface';
import unwrapAdditionalVariables from '../CreateProduct/unwrapAdditionalVariables';

const EMPTY_LIST: never[] = [];

/** A redux thunk/action dispatch that resolves to the action's return value. */
type ThunkDispatch = <TResult = unknown>(action: unknown) => Promise<TResult>;

/** An auto-id master config entry (universal or brand-scoped) as returned by the AutoIdGenerator action. */
export interface AutoIdMasterEntry {
  refCode?: string;
  isModuleActive?: boolean;
  counterNo?: string;
  brandId?: string | { toString(): string };
  selectedChildProperties?: unknown[];
}

/** The redux slices this hook reads. Keys mirror `reducers/index.js`. */
interface InlineRootState {
  UserDetails?: { userInfo?: { userId?: string } };
  SystemMastersDetails?: Record<string, unknown>;
  ColorLibraryDetails?: { ColorWaysLibList?: unknown };
  PomDetails?: { GradingList?: unknown };
  BomLibraryDetails?: { ReferenceSupplier?: unknown; supplierFactories?: unknown };
}

/** Redux-derived inputs the auto-id generator + payload assembly need (assembled by the page). */
export interface InlineContext {
  autoIdMaster: AutoIdMasterEntry[];
  monthOptions: RefEntity[];
  yearOptions: RefEntity[];
  brandList: RefEntity[];
  subBrandList: RefEntity[];
  seasonList: RefEntity[];
  category1List: RefEntity[];
  category2List: RefEntity[];
  category3List: RefEntity[];
  category4List: RefEntity[];
  productTypeList: RefEntity[];
  colorWaysLibList: RefEntity[];
  multiCustomAttributes: MultiCustomAttribute[];
}

export interface InlineOptions {
  selectOptions: SelectOptionMap;
  /** Grading master, for deciding whether a brand change costs a product its grading groups. */
  gradingLibrary: GradingLibraryEntry[];
  multiCustomAttributes: MultiCustomAttribute[];
  /**
   * SINGLE custom-attribute columns to render — the FULL set of attributes (fetched once,
   * unfiltered) so every custom field is shown by default on add, even before brand+PT. Each
   * carries the config (type/isActive/isEditable/values) the columns need.
   */
  customAttributes: DynamicColumn[];
  /**
   * Ids of the single custom attributes VALID for the active row's brand+PT (from the
   * brand+PT-scoped fetch). A custom cell is enabled only when its id is in this set — before a
   * brand+PT is chosen the set is empty, so every custom field renders disabled.
   */
  context: InlineContext;
  /** rangeId → its active size ids, for auto-selecting a range's sizes when Size Range changes. */
  sizeIdsByRange: Record<string, string[]>;
  optionsForRow: (row: TableRow | undefined, field: string) => RefEntity[] | undefined;
  /** Re-fetch the full single custom-attribute master (call after adding a "+" option). */
  refetchCustomAttributes: () => Promise<void>;
  allAdditionalVariables: ScopedAdditionalVariable[] | null;
  /** Whether every open row's dependent dropdown options have finished loading. */
  rowOptionsReady: boolean;
  allAdditionalVariablesMap: Record<string, string>;
  /** Tenant setting: Category 1 is scoped to the product type, so a type change invalidates the category chain. */
  catBasedOnProductType: boolean;
}

/**
 * Load every option list needed by the inline add/edit cells and compute the
 * cascaded/derived option sets (category 3-4, sizes from selected size ranges,
 * factories from selected suppliers). Options are keyed by the row field name.
 * @param inlineRow the active draft/edit row (source of cascade parents)
 * @returns per-field option lists keyed by row field name
 */
export const useInlineOptions = (inlineRow: TableRow | null, openRows: TableRow[] = []): InlineOptions => {
  const dispatch = useDispatch() as ThunkDispatch;
  const { UserDetails, SystemMastersDetails, ColorLibraryDetails, BomLibraryDetails, PomDetails } = useSelector(
    (redState: InlineRootState) => ({
      UserDetails: redState.UserDetails,
      SystemMastersDetails: redState.SystemMastersDetails,
      ColorLibraryDetails: redState.ColorLibraryDetails,
      BomLibraryDetails: redState.BomLibraryDetails,
      PomDetails: redState.PomDetails,
    }),
    shallowEqual,
  );
  const userId = UserDetails?.userInfo?.userId;

  const [scopedCategoriesLoaded, setScopedCategoriesLoaded] = useState(false);
  /* Category levels whose unscoped fallback has already been requested. */
  const fallbackCategoryTypesRef = useRef<Set<number>>(new Set());

  /**
   * (Re)read the FULL custom-attribute master — the same read the Provider makes, so on mount the
   * two share one request. Forced, because Create Product writes a brand+PT-scoped list into the same
   * store slice and a still-fresh scoped copy would hide columns. Also called after a "+" option is
   * added, so the new value appears without a page refresh.
   */
  const refetchCustomAttributes = useCallback(async () => {
    await dispatch(fetchMaster('customAttributeFreeText', undefined, { force: true }));
  }, [dispatch]);

  useEffect(() => {
    refetchCustomAttributes();
  }, [refetchCustomAttributes]);
  const {
    brandList = EMPTY_LIST,
    pcTypeList = EMPTY_LIST,
    seasonList = EMPTY_LIST,
    licensorList = EMPTY_LIST,
    category1List = EMPTY_LIST,
    category2List = EMPTY_LIST,
    category3List = EMPTY_LIST,
    category4List = EMPTY_LIST,
    catOptions1 = EMPTY_LIST,
    catOptions2 = EMPTY_LIST,
    catOptions3 = EMPTY_LIST,
    catOptions4 = EMPTY_LIST,
    productStatusList = EMPTY_LIST,
    lengthsList = EMPTY_LIST,
    sizeRangesMasterList = EMPTY_LIST,
    pcpCustomAttribute = EMPTY_LIST,
    customAttributeList = EMPTY_LIST,
    autoIdMaster = EMPTY_LIST,
    catBasedOnPT = false,
  } = SystemMastersDetails || {};
  /*
   * Tenant setting deciding whether Category 1/2 are scoped (to product type / Category 1) or the
   * full masters apply — mirrors CreateProduct's catBasedOnPTData gate.
   */
  const catBasedOnProductType = Boolean(catBasedOnPT);
  const colourwayList = ColorLibraryDetails?.ColorWaysLibList || EMPTY_LIST;
  const referenceSupplier = BomLibraryDetails?.ReferenceSupplier || EMPTY_LIST;
  /* Grading master — needed to tell whether a brand change costs a product its grading groups. */
  const gradingLibrary = (PomDetails?.GradingList as GradingLibraryEntry[]) || EMPTY_LIST;
  /* Read inside the mount effect without making the supplier list one of its dependencies. */
  const referenceSupplierRef = useRef<RefEntity[]>([]);
  referenceSupplierRef.current = referenceSupplier as RefEntity[];
  const supplierFactories = BomLibraryDetails?.supplierFactories || EMPTY_LIST;

  useEffect(() => {
    if (!userId) return;
    dispatch(fetchMaster('brands', { loggedInUserId: userId, isActive: 1 }));
    dispatch(fetchMaster('seasons', { loggedInUserId: userId }));
    dispatch(fetchMaster('pcTypes', { loggedInUserId: userId, isActive: true, tier: 'Tier1' }));
    dispatch(fetchMaster('productStatus', { loggedInUserId: userId, isActive: 1 }));
    /* Categories come from `fetch-categories`, which scopes the result to the user's assigned
       categories. `getTypeBasedCatgeory` is NOT a second copy of the same data — it returns every
       category of the type with no access filtering at all — so it is only ever a fallback for a
       user whose scoped list comes back empty, exactly as Create Product treats it. Fetching it
       up front both cost four requests per mount and, because pickCat prefers it, showed a
       restricted user every category in the company. */
    Promise.all([
      dispatch(fetchMaster('category1', { categoryType: 1, loggedInUserId: userId, isActive: true })),
      dispatch(fetchMaster('category2', { categoryType: 2, loggedInUserId: userId, isActive: true })),
      dispatch(fetchMaster('category3', { categoryType: 3, loggedInUserId: userId, isActive: true })),
      dispatch(fetchMaster('category4', { categoryType: 4, loggedInUserId: userId, isActive: true })),
    ]).then(() => setScopedCategoriesLoaded(true));
    dispatch(fetchMaster('lengths', { isActive: true, loggedInUserId: userId }));
    dispatch(fetchMaster('sizeRanges', { loggedInUserId: userId }));
    dispatch(fetchMaster('multiCustomAttribute', { isActive: true }));
    dispatch(fetchMaster('licensor', { loggedInUserId: userId, isActive: true }));
    dispatch(fetchMaster('colourList', { loggedInUserId: userId }));
    /* Auto-ID config drives the auto product-number generation (Section 4). */
    dispatch(fetchMaster('autoIdGenerator'));
    /* Grading master: brand changes need it to know which grading groups survive. */
    dispatch(getAllGradings({ isDropdawn: true, loggedInUserId: userId }));
    /* The factories fetch is keyed off the supplier ids, so it must still run when the supplier
       list was served from the store — `fetchMaster` returns false on a skip, not a response. */
    const loadSuppliers = async () => {
      const res = await dispatch<{ code?: number; data?: RefEntity[] } | false>(fetchMaster('referenceSupplier'));
      const suppliers = res && res.code === 200 ? res.data : referenceSupplierRef.current;
      if (Array.isArray(suppliers) && suppliers.length) {
        dispatch(fetchSuppliersFactories(suppliers.map((entry: RefEntity) => entry?._id)));
      }
    };
    loadSuppliers();
  }, [dispatch, userId]);

  /**
   * Load the unscoped category master only for the levels whose access-scoped list came back
   * empty. Requested types are remembered so a later store update cannot re-fire the request.
   */
  useEffect(() => {
    if (!scopedCategoriesLoaded) return;
    const scopedLists: Array<[number, unknown]> = [
      [1, category1List],
      [2, category2List],
      [3, category3List],
      [4, category4List],
    ];
    scopedLists.forEach(([categoryType, scopedList]) => {
      if (toOptions(scopedList).length || fallbackCategoryTypesRef.current.has(categoryType)) return;
      fallbackCategoryTypesRef.current.add(categoryType);
      dispatch(getTypeBasedCatgeory({ categoryType, isActive: true }));
    });
  }, [scopedCategoriesLoaded, category1List, category2List, category3List, category4List, dispatch]);

  const brandId = inlineRow?.brandId as string | undefined;

  const cat1 = inlineRow?.catOneId as string | undefined;
  const cat2 = inlineRow?.catTwoId as string | undefined;
  const cat3 = inlineRow?.catThreeId as string | undefined;
  const selectedSizeRanges = (inlineRow?.sizeRangeId as string[]) || EMPTY_LIST;
  const selectedSuppliers = (inlineRow?.suppliers as string[]) || EMPTY_LIST;

  /**
   * Prefer the getTypeBasedCatgeory source (catOptions*) — the Create Product page's
   * primary; fall back to the getCategory*Details list when it is empty.
   */
  const pickCat = (primary: unknown, fallback: unknown): RefEntity[] => {
    const primaryOptions = toOptions(primary);
    return primaryOptions.length ? primaryOptions : toOptions(fallback);
  };
  const cat1Source = useMemo(() => pickCat(catOptions1, category1List), [catOptions1, category1List]);
  const cat2Source = useMemo(() => pickCat(catOptions2, category2List), [catOptions2, category2List]);
  const cat3Source = useMemo(() => pickCat(catOptions3, category3List), [catOptions3, category3List]);
  const cat4Source = useMemo(() => pickCat(catOptions4, category4List), [catOptions4, category4List]);

  const cat3Options = useMemo(() => cat3For(cat3Source, cat1, cat2), [cat3Source, cat1, cat2]);

  const cat4Options = useMemo(() => cat4For(cat4Source, cat1, cat2, cat3), [cat4Source, cat1, cat2, cat3]);

  const sizeOptions = useMemo(() => sizesFor(sizeRangesMasterList, selectedSizeRanges), [sizeRangesMasterList, selectedSizeRanges]);

  /*
   * rangeId -> its ACTIVE (non-deleted) size ids. Used to auto-select/deselect a range's sizes when
   * the Size Range multi-select changes, mirroring CreateProduct's sizeRangeId case (add a range →
   * union its sizes into Size(s); remove a range → drop exactly its sizes).
   */
  const sizeIdsByRange = useMemo(() => {
    const map: Record<string, string[]> = {};
    toOptions(sizeRangesMasterList).forEach((range) => {
      const sizes = toOptions((range as unknown as { sizes?: unknown }).sizes)
        .filter((size) => !(size as unknown as { isDeleted?: boolean }).isDeleted && (size as unknown as { isActive?: boolean }).isActive)
        .map((size) => String(size._id));
      map[String(range._id)] = sizes;
    });
    return map;
  }, [sizeRangesMasterList]);

  const factoryOptions = useMemo(() => factoriesFor(supplierFactories, selectedSuppliers), [supplierFactories, selectedSuppliers]);

  const transportOptions = useMemo(
    () =>
      toOptions(transportArray).map((item) => ({
        _id: String((item as unknown as { name?: string }).name ?? ''),
        name: (item as unknown as { label?: string }).label ?? '',
      })),
    [],
  );

  const multiCustomAttributes = useMemo(() => (Array.isArray(pcpCustomAttribute) ? (pcpCustomAttribute as MultiCustomAttribute[]) : []), [pcpCustomAttribute]);

  /**
   * Flatten the single custom-attribute master into per-field configs (type/isActive/isEditable/
   * values) the same way ProductModal does (converDropdownData) — this is the source that lets
   * the dynamic columns gate correctly on BOTH add and edit.
   * converDropdownData filters strictly on `isLable`; if that yields nothing while the raw list
   * DOES have entries (data shape without a clean isLable flag), fall back to the "label/config"
   * rows (those without a lableId — the same split the fetch action uses) so columns still appear.
   * Flatten a raw custom-attribute list into per-field column configs (same shape ProductModal
   * builds via converDropdownData), with the fallback to !lableId "label" rows if the strict
   * converDropdownData filter yields nothing for this data shape.
   */
  const flattenCustomAttributes = (rawList: Array<Record<string, unknown>>): DynamicColumn[] => {
    if (!Array.isArray(rawList) || rawList.length === 0) return [];
    const flattened = converDropdownData(rawList) as DynamicColumn[];
    if (Array.isArray(flattened) && flattened.length > 0) return flattened;
    return rawList.filter((item) => !item.lableId) as unknown as DynamicColumn[];
  };

  /* COLUMNS source: the full unfiltered set, so every custom column shows on add before any brand/PT is chosen. */
  const customAttributes = useMemo(() => flattenCustomAttributes(customAttributeList as Array<Record<string, unknown>>), [customAttributeList]);

  /** Country options for Countries Sold — slug as the stored value, country name as the label. */
  const countryOptions = useMemo(() => CountriesList.map((name: string) => ({ _id: name.toLowerCase().replace(/\s+/g, '_'), name })), []);

  /*
   * Parent-scoped masters, cached by the parent id they were fetched for.
   *
   * The reducer holds ONE list — whatever the last cascade fetched — which is why every open row
   * was offered the active row's sub-brands / licenses / likenesses. Each distinct parent across
   * the open rows is fetched once and kept here, so a row resolves its own list. `pendingRef`
   * keeps a parent from being requested twice while its first request is still in flight.
   */
  const [subBrandsByBrand, setSubBrandsByBrand] = useState<Record<string, RefEntity[]>>({});
  const [licensesByLicensor, setLicensesByLicensor] = useState<Record<string, RefEntity[]>>({});
  const [likenessByLicense, setLikenessByLicense] = useState<Record<string, RefEntity[]>>({});
  const allSubBrands = useMemo(() => Object.values(subBrandsByBrand).flat(), [subBrandsByBrand]);
  const allLicenses = useMemo(() => Object.values(licensesByLicensor).flat(), [licensesByLicensor]);
  const allLikenesses = useMemo(() => Object.values(likenessByLicense).flat(), [likenessByLicense]);
  const pendingRef = useRef<Set<string>>(new Set());
  /*
   * The whole Product Type Process master, read once and filtered per row by `resolveRowOptions`, so
   * rows with different categories never cost one request each.
   */
  const [allProductTypeProcesses, setAllProductTypeProcesses] = useState<RefEntity[]>(EMPTY_LIST);
  /** Every Variable 2 config, read once; each row's match is resolved locally (null until loaded). */
  const [allAdditionalVariables, setAllAdditionalVariables] = useState<ScopedAdditionalVariable[] | null>(null);
  const [processesSettled, setProcessesSettled] = useState(false);
  const [variablesSettled, setVariablesSettled] = useState(false);
  const allAdditionalVariablesMap = useMemo(() => {
    const optionNameById: Record<string, string> = {};
    (allAdditionalVariables ?? []).forEach((variable) =>
      (variable.options ?? []).forEach((option) => {
        optionNameById[String(option._id)] = option.name ?? '';
      }),
    );
    return optionNameById;
  }, [allAdditionalVariables]);

  const cascadeRows = useMemo(() => (inlineRow ? [...openRows, inlineRow] : openRows), [openRows, inlineRow]);

  /**
   * True once every open row's dependent option lists have arrived (or failed — a failed read settles
   * as empty so a loader can never hang): sub-brands per brand, licenses/likenesses per licensor, the
   * process master when any row has a product type, and the Variable 2 configs.
   */
  const rowOptionsReady = useMemo(
    () =>
      !userId ||
      (variablesSettled &&
        cascadeRows.every((row) => {
          const brand = String(row?.brandId ?? '');
          const licensor = String(row?.licensorId ?? '');
          const license = String(row?.licenseId ?? '');
          return (
            (!brand || brand in subBrandsByBrand) &&
            (!licensor || licensor in licensesByLicensor) &&
            (!licensor || !license || likenessCacheKey(licensor, license) in likenessByLicense) &&
            (!row?.supplierType || processesSettled)
          );
        })),
    [userId, variablesSettled, cascadeRows, subBrandsByBrand, licensesByLicensor, likenessByLicense, processesSettled],
  );

  useEffect(() => {
    if (!userId) return;
    const brandIds = new Set<string>();
    const licensorIds = new Set<string>();
    const licensePairs = new Set<string>();
    cascadeRows.forEach((row) => {
      const rowBrand = String(row?.brandId ?? '');
      if (rowBrand) brandIds.add(rowBrand);
      const rowLicensor = String(row?.licensorId ?? '');
      if (rowLicensor) licensorIds.add(rowLicensor);
      const rowLicense = String(row?.licenseId ?? '');
      if (rowLicensor && rowLicense) licensePairs.add(`${rowLicensor}|${rowLicense}`);
    });

    const missingBrandIds = [...brandIds].filter((id) => !pendingRef.current.has(`brand:${id}`));
    if (missingBrandIds.length) {
      missingBrandIds.forEach((id) => pendingRef.current.add(`brand:${id}`));
      getAllSubBrand({ brandIds: missingBrandIds.join(','), loggedInUserId: userId })
        .then((res) => {
          const grouped: Record<string, RefEntity[]> = Object.fromEntries(missingBrandIds.map((id) => [id, []]));
          toOptions(res?.data).forEach((subBrand) => grouped[String((subBrand as RefEntity & { brandId?: unknown }).brandId)]?.push(subBrand));
          setSubBrandsByBrand((prev) => ({ ...prev, ...grouped }));
        })
        .catch(() => {
          missingBrandIds.forEach((id) => pendingRef.current.delete(`brand:${id}`));
          setSubBrandsByBrand((prev) => ({ ...Object.fromEntries(missingBrandIds.map((id) => [id, []])), ...prev }));
        });
    }

    licensorIds.forEach((id) => {
      const key = `licensor:${id}`;
      if (pendingRef.current.has(key)) return;
      pendingRef.current.add(key);
      Promise.resolve(dispatch(getLicenseDetails({ licensorId: id, loggedInUserId: userId, isActive: true })))
        .then((data) => setLicensesByLicensor((prev) => ({ ...prev, [id]: toOptions(data) })))
        .catch(() => {
          pendingRef.current.delete(key);
          setLicensesByLicensor((prev) => ({ [id]: [], ...prev }));
        });
    });

    licensePairs.forEach((pair) => {
      const key = `likeness:${pair}`;
      if (pendingRef.current.has(key)) return;
      pendingRef.current.add(key);
      const [rowLicensor, rowLicense] = pair.split('|');
      Promise.resolve(dispatch(getLikenessDetails({ licensorId: rowLicensor, licenseId: rowLicense, loggedInUserId: userId, isActive: true })))
        .then((data) => setLikenessByLicense((prev) => ({ ...prev, [pair]: toOptions(data) })))
        .catch(() => {
          pendingRef.current.delete(key);
          setLikenessByLicense((prev) => ({ [pair]: [], ...prev }));
        });
    });
    if (cascadeRows.some((row) => row?.supplierType) && !pendingRef.current.has('ptp:all')) {
      pendingRef.current.add('ptp:all');
      getAllProductTypeProcesses({ loggedInUserId: userId, isActive: 1, all: true })
        .then((res: { data?: unknown }) => setAllProductTypeProcesses(toOptions(res?.data)))
        .catch(() => pendingRef.current.delete('ptp:all'))
        .finally(() => setProcessesSettled(true));
    }
  }, [dispatch, userId, cascadeRows]);

  useEffect(() => {
    if (!userId) return;
    getAllAdditionalVariables({ loggedInUserId: userId })
      .then((res) => setAllAdditionalVariables(unwrapAdditionalVariables(res as NestedThunkEnvelope<AdditionalVariable[]>) as ScopedAdditionalVariable[]))
      .catch(() => setAllAdditionalVariables(null))
      .finally(() => setVariablesSettled(true));
  }, [userId]);

  /* Month/year option lists for the auto-id resolver (mirrors CreateProduct's construction). */
  const monthOptions = useMemo(() => createOptions(monthsPlaceholder, 'name', '_id'), []);
  const yearOptions = useMemo(() => createOptions(getYears(), 'name', '_id'), []);

  const selectOptions = useMemo<SelectOptionMap>(() => {
    const options: SelectOptionMap = {
      brandId: toOptions(brandList),
      subBrandId: allSubBrands,
      supplierType: toOptions(pcTypeList),
      seasonId: toOptions(seasonList),
      month: monthOptions,
      year: yearOptions,
      licensorId: toOptions(licensorList),
      licenseId: allLicenses,
      likenessId: allLikenesses,
      catOneId: cat1Source,
      catTwoId: cat2Source,
      catThreeId: cat3Options,
      catFourId: cat4Options,
      status: toOptions(productStatusList),
      legLengths: toOptions(lengthsList),
      colourLibraries: toOptions(colourwayList),
      sizeRangeId: toOptions(sizeRangesMasterList),
      sizeRangeSizes: sizeOptions,
      suppliers: toOptions(referenceSupplier),
      supplierFactories: factoryOptions,
      transport: transportOptions,
      productTypeProcesses: allProductTypeProcesses,
      soldCountries: countryOptions,
    };

    /* Per-attribute option lists keyed by mca_<id>, so multi-custom columns can render. */
    multiCustomAttributes.forEach((attribute) => {
      options[`mca_${attribute._id}`] = (attribute.values || []).map((value) => ({ _id: value._id, name: value.keyName ?? '' }));
    });
    return options;
  }, [
    brandList,
    allSubBrands,
    pcTypeList,
    seasonList,
    monthOptions,
    yearOptions,
    licensorList,
    allLicenses,
    allLikenesses,
    cat1Source,
    cat2Source,
    cat3Options,
    cat4Options,
    productStatusList,
    lengthsList,
    colourwayList,
    sizeRangesMasterList,
    sizeOptions,
    referenceSupplier,
    factoryOptions,
    transportOptions,
    allProductTypeProcesses,
    countryOptions,
    multiCustomAttributes,
  ]);

  const cascadeSources: CascadeSources = useMemo(
    () => ({
      subBrandsByBrand,
      licensesByLicensor,
      likenessByLicense,
      productTypeProcesses: allProductTypeProcesses,
      cat1Source,
      cat2Source,
      catBasedOnProductType,
      cat3Source,
      cat4Source,
      sizeRangeMaster: sizeRangesMasterList,
      supplierFactories,
      additionalVariables: allAdditionalVariables ?? EMPTY_LIST,
      seasonSource: toOptions(seasonList),
    }),
    [
      subBrandsByBrand,
      licensesByLicensor,
      likenessByLicense,
      allProductTypeProcesses,
      cat1Source,
      cat2Source,
      catBasedOnProductType,
      cat3Source,
      cat4Source,
      sizeRangesMasterList,
      supplierFactories,
      allAdditionalVariables,
      seasonList,
    ],
  );

  const optionsForRow = useCallback(
    (row: TableRow | undefined, field: string): RefEntity[] | undefined => resolveRowOptions(row, field, cascadeSources),
    [cascadeSources],
  );

  const context = useMemo<InlineContext>(
    () => ({
      autoIdMaster: Array.isArray(autoIdMaster) ? (autoIdMaster as AutoIdMasterEntry[]) : [],
      monthOptions,
      yearOptions,
      brandList: toOptions(brandList),
      subBrandList: subBrandsByBrand[String(brandId ?? '')] ?? EMPTY_LIST,
      seasonList: toOptions(seasonList),
      category1List: cat1Source,
      category2List: cat2Source,
      category3List: cat3Source,
      category4List: cat4Source,
      productTypeList: toOptions(pcTypeList),
      colorWaysLibList: toOptions(colourwayList),
      multiCustomAttributes,
    }),
    [
      autoIdMaster,
      monthOptions,
      yearOptions,
      brandList,
      subBrandsByBrand,
      brandId,
      seasonList,
      cat1Source,
      cat2Source,
      cat3Source,
      cat4Source,
      pcTypeList,
      colourwayList,
      multiCustomAttributes,
    ],
  );

  return {
    selectOptions,
    optionsForRow,
    multiCustomAttributes,
    customAttributes,
    context,
    sizeIdsByRange,
    refetchCustomAttributes,
    gradingLibrary,
    allAdditionalVariables,
    allAdditionalVariablesMap,
    rowOptionsReady,
    catBasedOnProductType,
  };
};
